import { Plus } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import {
  useCalendarActions,
  useWaitlist,
  type WaitlistEntry,
} from '@/features/calendar/api';
import type { PatientSummary } from '@/features/patients/types';
import { useServices } from '@/features/queue/api';
import { isApiError } from '@/lib/api';
import { PatientPicker } from '@/pages/queue/PatientPicker';
import { bookingConflict } from './booking-format';
import { dayLabel, klTime, whenLabel } from './time';
import styles from './calendar.module.css';
import { clinicDate } from '@/lib/clinic-time';

/**
 * "Calendar · waitlist" (#19): patients waiting for an earlier slot. When a
 * slot frees, the first match is offered for 15 minutes, then it moves on.
 * Patients join from the app; on staging the counter adds them and accepts
 * an offer on their behalf.
 */
export function WaitlistTab() {
  const { hasPermission } = useSession();
  const canManage = hasPermission('appointments:manage');
  const { data, isLoading, error, refetch } = useWaitlist();
  const a = useCalendarActions();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const fail = (err: unknown) => {
    // RL-1044/1045: name the overlapping booking's time.
    const conflict = bookingConflict(err);
    toast.show(
      {
        tone: 'error',
        title:
          conflict?.title ??
          (isApiError(err) ? (err.problem?.title ?? 'Not done') : 'Not done'),
        body: conflict?.message ?? (isApiError(err) ? err.message : undefined),
        error: err,
      },
      conflict ? 10_000 : undefined,
    );
  };

  const columns: Column<WaitlistEntry>[] = [
    {
      key: 'ref',
      header: 'Ref',
      render: (w) => <span className={styles.mono}>{w.number}</span>,
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (w) => <strong>{w.patient.fullName}</strong>,
    },
    {
      key: 'wants',
      header: 'Wants',
      render: (w) => (
        <>
          <p>{w.service.name}</p>
          <p className={styles.sub}>{w.wants}</p>
        </>
      ),
    },
    {
      key: 'since',
      header: 'Since',
      render: (w) => dayLabel(clinicDate(w.since)),
    },
    {
      key: 'status',
      header: 'Status',
      render: (w) =>
        w.offer ? (
          <>
            <Badge tone="warning">Offered</Badge>
            <p className={styles.sub}>
              {whenLabel(w.offer.startsAt)} · until {klTime(w.offer.expiresAt)}
            </p>
          </>
        ) : (
          <Badge>Waiting</Badge>
        ),
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (w) =>
        canManage && (
          <div className={styles.actions}>
            {w.offer && (
              <Button
                size="sm"
                variant="primary"
                loading={a.acceptOffer.isPending}
                onClick={() =>
                  a.acceptOffer
                    .mutateAsync(w.id)
                    .then((r) =>
                      toast.show({
                        tone: 'success',
                        title: `Booked ${r.booked ? whenLabel(r.booked.startsAt) : ''}`,
                      }),
                    )
                    .catch(fail)
                }
              >
                Accept offer
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => a.removeWaitlist.mutateAsync(w.id).catch(fail)}
            >
              Remove
            </Button>
          </div>
        ),
    },
  ];

  return (
    <Card
      title="Waiting for an earlier slot"
      flush
      actions={
        <>
          <span className={styles.sub}>
            When a slot frees up it is offered to the first patient; the offer
            expires after 15 minutes, then moves on
          </span>
          {canManage && (
            <Button
              size="sm"
              icon={<Plus size={14} />}
              onClick={() => setAdding(true)}
            >
              Add
            </Button>
          )}
        </>
      }
    >
      <DataTable
        caption="Waitlist"
        columns={columns}
        rows={data}
        rowKey={(w) => w.id}
        loading={isLoading}
        error={error}
        onRetry={() => void refetch()}
        empty={
          <EmptyState
            title="Nobody is waiting"
            body="Patients waiting for an earlier slot appear here."
          />
        }
      />
      <AddWaitlist open={adding} onClose={() => setAdding(false)} />
    </Card>
  );
}

function AddWaitlist({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { data: services } = useServices();
  const a = useCalendarActions();
  const toast = useToast();
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [service, setService] = useState('');
  const [wants, setWants] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add to the waitlist"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!patient || !service || wants.trim().length < 2}
            loading={a.addWaitlist.isPending}
            onClick={() =>
              a.addWaitlist
                .mutateAsync({
                  patientId: patient!.id,
                  serviceId: service,
                  wants: wants.trim(),
                  fromDate: from || undefined,
                  toDate: to || undefined,
                })
                .then((w) => {
                  toast.show({ tone: 'success', title: `${w.number} added` });
                  setPatient(null);
                  setWants('');
                  onClose();
                })
                .catch((err: unknown) =>
                  toast.show({
                    tone: 'error',
                    title: 'Not added',
                    body: isApiError(err) ? err.message : undefined,
                    error: err,
                  }),
                )
            }
          >
            Add to waitlist
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        <PatientPicker value={patient} onChange={setPatient} />
        <Field label="Service">
          <Select
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="Choose the service"
            options={(services ?? []).map((s) => ({
              value: s.id,
              label: s.name,
            }))}
          />
        </Field>
        <Field label="What they want">
          <Input
            value={wants}
            placeholder="Any earlier slot this week"
            onChange={(e) => setWants(e.target.value)}
          />
        </Field>
        <div className={styles.split}>
          <Field label="From (optional)">
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Field>
          <Field label="To (optional)">
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}
